/**
 * PageShell — Standardized full-width wrapper for every migrated page.
 *
 *   ┌─ Hero (full bleed) ───────────────────────────────┐
 *   │                                                    │
 *   ├────────────────────────────────────────────────────┤
 *   │  optional <SectionTabs>                            │
 *   ├────────────────────────────────────────────────────┤
 *   │ [main content]                       │ [right rail │
 *   │                                      │  TOC]       │
 *   └──────────────────────────────────────┴─────────────┘
 *
 * Renders its own root so it deliberately escapes the legacy
 * `container mx-auto max-w-*` wrappers older pages still use.
 *
 * Props:
 *   - hero:    ReactNode — typically <PageHero> or <PageHeroCompact>
 *   - tabs:    ReactNode — optional <SectionTabs>
 *   - rightRail: ReactNode — typically <RightRailToc> for long pages
 *   - theme:   section theme; controls body tint
 *   - children: page body
 *   - stickyTitle: optional string surfaced in the sticky-on-scroll
 *                  strip when the user has scrolled past the hero.
 *   - stickyActions: optional ReactNode for the right-side of the
 *                    sticky strip (e.g. a small CTA cluster).
 *   - stickyDisabled: pass `true` to opt out entirely (e.g. on
 *                     pages with a custom carousel hero).
 */
import React, { useRef } from 'react';
import { cn } from '../../lib/utils';
import { StickyHeroStrip } from './StickyHeroStrip';

export function PageShell({
  hero,
  tabs,
  rightRail,
  theme,
  children,
  testId = 'page-shell',
  bodyClassName,
  stickyTitle,
  stickyActions,
  stickyDisabled = false,
}) {
  const sentinelRef = useRef(null);
  return (
    // Negative margins escape the parent <main className="p-6 md:p-8">
    // so the hero touches the screen edges. The body re-introduces
    // padding inside the constrained inner container.
    <div
      className={cn(
        '-mx-6 md:-mx-8 -my-6 md:-my-8 min-h-[calc(100vh-4rem)] flex flex-col',
        theme?.bodyTint || 'bg-slate-50',
      )}
      data-testid={testId}
    >
      {!stickyDisabled && stickyTitle && (
        <StickyHeroStrip
          title={stickyTitle}
          actions={stickyActions}
          theme={theme}
          triggerRef={sentinelRef}
          testId={`${testId}-sticky`}
        />
      )}
      {hero}
      {/* Sentinel sits at the bottom of the hero. The sticky
          strip fades in once this element scrolls above the
          viewport — see StickyHeroStrip for the IO logic. */}
      <div ref={sentinelRef} aria-hidden="true" />
      {tabs}
      <div
        className={cn(
          'flex-1 w-full px-4 md:px-8 lg:px-12 py-6 md:py-8',
          rightRail ? 'lg:flex lg:gap-8 lg:items-start' : '',
        )}
      >
        <div
          className={cn(
            'min-w-0',
            rightRail ? 'lg:flex-1' : '',
            bodyClassName,
          )}
          data-testid={`${testId}-body`}
        >
          {children}
        </div>
        {rightRail && (
          <aside
            className="hidden lg:block w-60 xl:w-64 shrink-0"
            data-testid={`${testId}-rail`}
          >
            {rightRail}
          </aside>
        )}
      </div>
    </div>
  );
}

export default PageShell;
